import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, TextField, CircularProgress, Alert, IconButton } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DataTable from './DataTable';

// Helper to format date as YYYY-MM-DD
const formatDate = (date) => date.toISOString().split('T')[0];

const getApiTableName = (tableName) => tableName.toLowerCase().replace(/ /g, '-');

const CapacityComparison = ({ tableName }) => {
    const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Add state for fixed range data
    const [fixedRangeData, setFixedRangeData] = useState([]);
    const [fixedRangeLoading, setFixedRangeLoading] = useState(false);

    // Fetch data for the last 24 hours (7:00 AM prev day to 6:59 AM selected day)
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const end = new Date(selectedDate);
                const start = new Date(end);
                start.setDate(start.getDate() - 1);
                // Set times to 7:00:00 and 6:59:59
                start.setHours(7, 0, 0, 0);
                end.setHours(6, 59, 59, 999);
                const apiTableName = getApiTableName(tableName);
                const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
                const url = `${apiBaseUrl}/${apiTableName}/customrange?startDate=${encodeURIComponent(formatDate(start) + ' 07:00:00')}&endDate=${encodeURIComponent(formatDate(end) + ' 06:59:59')}`;
                const response = await fetch(url);
                if (!response.ok) throw new Error('Failed to fetch data');
                const result = await response.json();
                setData(result);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [tableName, selectedDate]);

    // Fetch fixed range data for the same date as comparison
    useEffect(() => {
        const fetchFixedRange = async () => {
            setFixedRangeLoading(true);
            try {
                const apiTableName = getApiTableName(tableName);
                const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
                const url = `${apiBaseUrl}/${apiTableName}/fixedrange?date=${selectedDate}`;
                const response = await fetch(url);
                if (!response.ok) throw new Error('Failed to fetch fixed range data');
                const result = await response.json();
                setFixedRangeData(result);
            } catch (err) {
                setFixedRangeData([]);
            } finally {
                setFixedRangeLoading(false);
            }
        };
        fetchFixedRange();
    }, [tableName, selectedDate]);

    // Calculate sums and get capacity fields
    const getComparisonData = () => {
        if (!data || data.length === 0) return null;
        // Try to find the correct keys
        const first = data[0];
        // Find keys for actual and target (capacity)
        let feedKey, condKey, powerKey, feedCapKey, condCapKey, powerCapKey;
        if (tableName.toLowerCase() === 'ks-textile') {
            feedKey = 'FEED_FLOW_PRE_1H_TOT_KS';
            condKey = 'COND_FLOW_PRE_1H_TOT_KS';
            powerKey = 'POWER_PRE_1H_TOT_KS';
            feedCapKey = 'Feed_Capacity';
            condCapKey = 'Cond_Capacity';
            powerCapKey = 'Power_Capacity';
        } else if (tableName.toLowerCase() === 'clear-choice') {
            feedKey = 'FEED_FLOW_TOT_PRE_1H_CCHOICE';
            condKey = 'COND_FLOW_TOT_PRE_1H_CCHOICE';
            powerKey = 'EM_POWER_TOT_PRE_1H_CCHOICE';
            feedCapKey = 'Feed_Capacity';
            condCapKey = 'Cond_Capacity';
            powerCapKey = 'Power_Capacity';
        } else if (tableName.toLowerCase() === 'baddi') {
            feedKey = null;
            condKey = null;
            powerKey = 'Baddi_Power_PRE_1h_TOT';
            feedCapKey = null;
            condCapKey = null;
            powerCapKey = null;
        } else if (tableName.toLowerCase() === 'parle') {
            feedKey = 'FEED_FLOW_PRE_1H_TOT_PARLE';
            condKey = 'COND_FLOW_TOT_PRE_1HR_PARLE';
            powerKey = 'POWER_PRE_1H_TOT_PARLE';
            feedCapKey = 'Feed_Capacity';
            condCapKey = 'Cond_Capacity';
            powerCapKey = 'Power_Capacity';
        } else {
            feedKey = Object.keys(first).find(k => k.toUpperCase().includes('FEED') && !k.toUpperCase().includes('CAPACITY'));
            condKey = Object.keys(first).find(k => k.toUpperCase().includes('COND') && !k.toUpperCase().includes('CAPACITY'));
            powerKey = Object.keys(first).find(k => k.toUpperCase().includes('POWER') && !k.toUpperCase().includes('CAPACITY'));
            feedCapKey = Object.keys(first).find(k => k.toUpperCase().includes('FEED') && k.toUpperCase().includes('CAPACITY'));
            condCapKey = Object.keys(first).find(k => k.toUpperCase().includes('COND') && k.toUpperCase().includes('CAPACITY'));
            powerCapKey = Object.keys(first).find(k => k.toUpperCase().includes('POWER') && k.toUpperCase().includes('CAPACITY'));
        }

        // Actuals
        const feedActual = feedKey ? data.reduce((sum, row) => sum + (Number(row[feedKey]) || 0), 0) : null;
        const condActual = condKey ? data.reduce((sum, row) => sum + (Number(row[condKey]) || 0), 0) : null;
        const powerActual = powerKey ? data.reduce((sum, row) => sum + (Number(row[powerKey]) || 0), 0) : null;

        // Targets (multiply by number of rows/events)
        const feedTarget = feedCapKey ? Number(first[feedCapKey]) * data.length : null;
        const condTarget = condCapKey ? Number(first[condCapKey]) * data.length : null;
        // For power, multiply by number of rows (same as feed/cond)
        const powerTarget = powerCapKey ? Number(first[powerCapKey]) * data.length : null;

        // Only show if at least one pair exists
        if (feedActual === null && condActual === null && powerActual === null) return null;

        return [
            feedActual !== null && feedTarget !== null ? { name: 'Feed Flow', Actual: feedActual, Target: feedTarget } : null,
            condActual !== null && condTarget !== null ? { name: 'Cond Flow', Actual: condActual, Target: condTarget } : null,
            powerActual !== null && powerTarget !== null ? { name: 'Power', Actual: powerActual, Target: powerTarget } : null,
        ].filter(Boolean);
    };

    const comparisonData = getComparisonData();

    // Split comparisonData for left and right axis
    const leftData = (comparisonData || []).filter(entry =>
        entry.name.toLowerCase().includes('feed') || entry.name.toLowerCase().includes('cond')
    );
    const rightData = (comparisonData || []).filter(entry =>
        entry.name.toLowerCase().includes('power')
    );

    // Combine into a single array with unique keys for BarChart
    // Each object will have: name, ActualFeedCond, TargetFeedCond, ActualPower, TargetPower
    const chartData = [
        ...leftData.map(entry => ({
            name: entry.name,
            ActualFeedCond: entry.Actual,
            TargetFeedCond: entry.Target
        })),
        ...rightData.map(entry => ({
            name: entry.name,
            ActualPower: entry.Actual,
            TargetPower: entry.Target
        }))
    ];

    // Calculate ratios for kW/m³
    const first = data[0];
    let feedKey, powerKey, feedCapKey, powerCapKey;
    if (data && data.length > 0) {
        if (tableName.toLowerCase() === 'ks-textile') {
            feedKey = 'FEED_FLOW_PRE_1H_TOT_KS';
            powerKey = 'POWER_PRE_1H_TOT_KS';
            feedCapKey = 'Feed_Capacity';
            powerCapKey = 'Power_Capacity';
        } else if (tableName.toLowerCase() === 'clear-choice') {
            feedKey = 'FEED_FLOW_TOT_PRE_1H_CCHOICE';
            powerKey = 'EM_POWER_TOT_PRE_1H_CCHOICE';
            feedCapKey = 'Feed_Capacity';
            powerCapKey = 'Power_Capacity';
        } else if (tableName.toLowerCase() === 'baddi') {
            feedKey = null;
            powerKey = 'Baddi_Power_PRE_1h_TOT';
            feedCapKey = null;
            powerCapKey = null;
        } else if (tableName.toLowerCase() === 'parle') {
            feedKey = 'FEED_FLOW_PRE_1H_TOT_PARLE';
            powerKey = 'POWER_PRE_1H_TOT_PARLE';
            feedCapKey = 'Feed_Capacity';
            powerCapKey = 'Power_Capacity';
        } else {
            feedKey = Object.keys(first).find(k => k.toUpperCase().includes('FEED') && !k.toUpperCase().includes('CAPACITY'));
            powerKey = Object.keys(first).find(k => k.toUpperCase().includes('POWER') && !k.toUpperCase().includes('CAPACITY'));
            feedCapKey = Object.keys(first).find(k => k.toUpperCase().includes('FEED') && k.toUpperCase().includes('CAPACITY'));
            powerCapKey = Object.keys(first).find(k => k.toUpperCase().includes('POWER') && k.toUpperCase().includes('CAPACITY'));
        }
    }

    let totalFeedActual = 0, totalFeedTarget = 0, totalPowerActual = 0, totalPowerTarget = 0;
    if (data && data.length > 0) {
        totalFeedActual = feedKey ? data.reduce((sum, row) => sum + (Number(row[feedKey]) || 0), 0) : 0;
        totalFeedTarget = feedCapKey && first ? Number(first[feedCapKey]) * data.length : 0;
        totalPowerActual = powerKey ? data.reduce((sum, row) => sum + (Number(row[powerKey]) || 0), 0) : 0;
        totalPowerTarget = powerCapKey && first ? Number(first[powerCapKey]) * data.length : 0;
    }
    const ratioTarget = totalFeedTarget && totalPowerTarget ? (totalPowerTarget / totalFeedTarget) : null;
    const ratioActual = totalFeedActual && totalPowerActual ? (totalPowerActual / totalFeedActual) : null;

    // Data for kW/m³ graph
    // Show Actual and Target as two bars side by side, both labeled (kW/m³)
    const ratioChartData = [
        {
            label: 'kW/m³',
            actual: ratioActual !== null ? Number(ratioActual.toFixed(2)) : null,
            target: ratioTarget !== null ? Number(ratioTarget.toFixed(2)) : null,
        }
    ];

    // Date change handler
    const handleDateChange = (e) => setSelectedDate(e.target.value);

    // Prev/Next day handlers
    const handlePrevDay = () => {
        const prev = new Date(selectedDate);
        prev.setDate(prev.getDate() - 1);
        setSelectedDate(prev.toISOString().split('T')[0]);
    };
    const handleNextDay = () => {
        const next = new Date(selectedDate);
        next.setDate(next.getDate() + 1);
        setSelectedDate(next.toISOString().split('T')[0]);
    };

    return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, maxWidth: 1400, width: '100%', mx: 'auto', borderRadius: 4, boxShadow: 4, background: '#fff' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 2, gap: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ letterSpacing: 1, fontWeight: 600 }}>
                   Capacity Comparison
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton aria-label="Previous day" onClick={handlePrevDay} size="small" sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <TextField
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        sx={{ minWidth: 140, bgcolor: '#fff', borderRadius: 2 }}
                        size="small"
                        inputProps={{ style: { textAlign: 'center', fontWeight: 500 } }}
                    />
                    <IconButton aria-label="Next day" onClick={handleNextDay} size="small" sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                Comparing sum of last 24h (7:00 AM prev day to 6:00 AM selected) with capacity.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 180 }}>
                    <CircularProgress />
                </Box>
            ) : comparisonData && comparisonData.length > 0 ? (
                <>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, width: '100%', maxWidth: 1200, justifyContent: 'center', alignItems: 'stretch', mb: 2, flexWrap: 'wrap', mx: 'auto' }}>
                        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 600, width: '100%', height: 320, background: '#f9f9f9', borderRadius: 3, p: 2, boxSizing: 'border-box', mx: 'auto' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                    barCategoryGap="20%"
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={{ fontWeight: 500, fontSize: 14 }} />
                                    <YAxis
                                        yAxisId="left"
                                        label={{
                                            value: 'Feed/Cond (m³)',
                                            angle: -90,
                                            position: "insideLeft",
                                            style: { textAnchor: 'middle', fontWeight: 600, fill: "#1976d2" }
                                        }}
                                        stroke="#1976d2"
                                        tick={{ fontWeight: 500 }}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        label={{
                                            value: 'Power (kW)',
                                            angle: 90,
                                            position: "insideRight",
                                            style: { textAnchor: 'middle', fontWeight: 600, fill: "#bfa100" }
                                        }}
                                        stroke="#bfa100"
                                        tick={{ fontWeight: 500 }}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, fontWeight: 500 }}
                                        formatter={(value) => Number(value).toFixed(2)}
                                    />
                                    {/* Feed/Cond bars */}
                                    <Bar dataKey="ActualFeedCond" name="Actual" yAxisId="left" isAnimationActive={false} radius={[6, 6, 0, 0]}>
                                        {chartData.map((entry, idx) => (
                                            <Cell
                                                key={`actual-cell-left-${idx}`}
                                                fill={
                                                    entry.name && entry.name.toLowerCase().includes('feed')
                                                        ? "#43a047"
                                                        : entry.name && entry.name.toLowerCase().includes('cond')
                                                        ? "#1976d2"
                                                        : "#cccccc"
                                                }
                                            />
                                        ))}
                                        <LabelList dataKey="ActualFeedCond" position="top" formatter={v => v !== undefined ? Number(v).toFixed(2) : ''} style={{ fontWeight: 600 }} />
                                    </Bar>
                                    <Bar dataKey="TargetFeedCond" name="Target" yAxisId="left" isAnimationActive={false} radius={[6, 6, 0, 0]}>
                                        {chartData.map((entry, idx) => (
                                            <Cell
                                                key={`target-cell-left-${idx}`}
                                                fill="#d32f2f"
                                            />
                                        ))}
                                        <LabelList dataKey="TargetFeedCond" position="top" formatter={v => v !== undefined ? Number(v).toFixed(2) : ''} style={{ fontWeight: 600 }} />
                                    </Bar>
                                    {/* Power bars */}
                                    <Bar dataKey="ActualPower" name="Actual" yAxisId="right" isAnimationActive={false} radius={[6, 6, 0, 0]}>
                                        {chartData.map((entry, idx) => (
                                            <Cell
                                                key={`actual-cell-right-${idx}`}
                                                fill="#bfa100"
                                            />
                                        ))}
                                        <LabelList dataKey="ActualPower" position="top" formatter={v => v !== undefined ? Number(v).toFixed(2) : ''} style={{ fontWeight: 600 }} />
                                    </Bar>
                                    <Bar dataKey="TargetPower" name="Target" yAxisId="right" isAnimationActive={false} radius={[6, 6, 0, 0]}>
                                        {chartData.map((entry, idx) => (
                                            <Cell
                                                key={`target-cell-right-${idx}`}
                                                fill="#d32f2f"
                                            />
                                        ))}
                                        <LabelList dataKey="TargetPower" position="top" formatter={v => v !== undefined ? Number(v).toFixed(2) : ''} style={{ fontWeight: 600 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 500, width: '100%', height: 320, background: '#f9f9f9', borderRadius: 3, p: 2, boxSizing: 'border-box', mx: 'auto' }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, letterSpacing: 1 }}>
                                (kW/m³)
                            </Typography>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart
                                    data={ratioChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                    barCategoryGap="10%"
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tick={{ fontWeight: 500, fontSize: 14 }} />
                                    <YAxis
                                        label={{
                                            value: 'kW/m³',
                                            angle: -90,
                                            position: "insideLeft",
                                            
                                            style: { textAnchor: 'middle', fontWeight: 600, fill: "#1976d2" }
                                        }}
                                        stroke="#1976d2"
                                        allowDecimals={true}
                                        tick={{ fontWeight: 500 }}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, fontWeight: 500 }}
                                        formatter={(value, name) => value !== null && value !== undefined ? Number(value).toFixed(2) : ''}
                                        labelFormatter={() => ''}
                                    />
                                    <Bar
                                        dataKey="actual"
                                        name="Actual"
                                        fill="#2e7d32"
                                        isAnimationActive={false}
                                        radius={[6, 6, 0, 0]}
                                        barSize={30}
                                    >
                                        <LabelList dataKey="actual" position="top" formatter={v => v !== undefined && v !== null ? Number(v).toFixed(2) : ''} style={{ fontWeight: 600 }} />
                                    </Bar>
                                    <Bar
                                        dataKey="target"
                                        name="Target"
                                        fill="#c62828"
                                        isAnimationActive={false}
                                        radius={[6, 6, 0, 0]}
                                        barSize={30}
                                    >
                                        <LabelList dataKey="target" position="top" formatter={v => v !== undefined && v !== null ? Number(v).toFixed(2) : ''} style={{ fontWeight: 600 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Box>
                    {/* Custom legend for all Target (capacity) colors below the graph */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2, mb: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 18, height: 18, bgcolor: '#43a047', borderRadius: 0.5, border: '1px solid #43a047' }} />
                            <Typography variant="body2">Feed</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 18, height: 18, bgcolor: '#1976d2', borderRadius: 0.5, border: '1px solid #1976d2' }} />
                            <Typography variant="body2">Cond</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 18, height: 18, bgcolor: '#bfa100', borderRadius: 0.5, border: '1px solid #bfa100' }} />
                            <Typography variant="body2">Power</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 18, height: 18, bgcolor: '#d32f2f', borderRadius: 0.5, border: '1px solid #d32f2f' }} />
                            <Typography variant="body2">Target</Typography>
                        </Box>
                    </Box>
                    {/* Show only the fixed range DataTable, not the graph */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, letterSpacing: 1 }}>
                            Fixed Range Data Table (7:00 AM - 6:00 AM)
                        </Typography>
                        <DataTable
                            data={fixedRangeData}
                            tableName={tableName}
                            loading={fixedRangeLoading}
                            hideCapacity={tableName.toLowerCase() === 'ks-textile' || tableName.toLowerCase() === 'clear-choice'}
                        />
                    </Box>
                </>
            ) : (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>No capacity data available for this site.</Typography>
            )}
        </Paper>
    );
};

export default CapacityComparison;
